import type { ForwardedRef } from 'react';
import { forwardRef } from 'react';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import type { BoxProps } from '../box/box';
import { Box, filterDisplayProps } from '../box/box';

import * as styles from './card.css';

export interface CardProps
  extends Pick<
    BoxProps,
    | 'padding'
    | 'spacing'
    | 'background'
    | 'stack'
    | 'className'
    | 'children'
    | 'Component'
    | 'method'
    | 'alignContent'
    | 'role'
    | 'htmlFor'
    | 'style'
    | 'expand'
    | 'constrain'
    | 'onClick'
  > {
  boxVariant?: BoxProps['variant'];
  variant?: keyof typeof styles.variant;
  'data-testid'?: string;
}

export const TEST_IDS = {
  CARD: 'card-component',
};

const borderPalette: Record<
  NonNullable<CardProps['variant']>,
  BoxProps['border']
> = {
  primary: 'neutral200',
  lightShadow: 'neutral350',
  darkShadow: undefined,
};

/**
 * Do not use the CardWithoutForwardRef export in the platform, use the Card export instead.
 *
 * This export is used to generate documentation for Hive only.
 */
export const CardWithoutForwardRef = <Element extends HTMLElement>(
  {
    variant = 'primary',
    boxVariant = 'flex',
    padding = 'basePos4',
    'data-testid': testId = TEST_IDS.CARD,
    background = 'neutral000',
    className,
    children,
    spacing,
    alignContent,
    stack,
    ...props
  }: CardProps,
  ref: ForwardedRef<Element>,
) => (
  <Box
    className={classFilter([styles.variant[variant], className])}
    border={borderPalette[variant]}
    background={background}
    data-testid={testId}
    padding={padding}
    ref={ref}
    rounded
    spacing={spacing}
    {...filterDisplayProps({ variant: boxVariant, alignContent, stack })}
    {...props}
  >
    {children}
  </Box>
);

// Manually reset the displayName in the component tree.
CardWithoutForwardRef.displayName = 'Card';

export const Card = forwardRef(CardWithoutForwardRef);
